
import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { DTypeService } from '../../core/dtype/dtype.service';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { CancelReason } from '../../modules/admin/tasks/tasks.types';
import { LocalizePipe } from '../../core/pipes/standalone/localize.pipe';

@Component({
    selector: 'app-cancel-workshift-dialog',
    templateUrl: './cancel-workshift-dialog.component.html',
    imports: [FormsModule, LocalizePipe, MatButtonModule, MatFormFieldModule, MatIconModule, MatSelectModule, ReactiveFormsModule, TranslocoModule],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CancelWorkshiftDialogComponent {

    control: FormControl = new FormControl('', Validators.required);

    private data: { workshiftsToCancel: string[]; workshiftsCantCancel: string[], marca_code: string } = inject(MAT_DIALOG_DATA);
    private dialogRef = inject(MatDialogRef<CancelWorkshiftDialogComponent>);
    private dtypeService = inject(DTypeService);
    private fuseConfirmationService = inject(FuseConfirmationService);
    private transloco = inject(TranslocoService);

    workshiftsToCancel = signal(this.data.workshiftsToCancel);
    workshiftsCantCancel = signal(this.data.workshiftsCantCancel);
    idsCantCancel = computed(() => this.workshiftsCantCancel().join(', '));
    reasonsData: Signal<CancelReason[]> = toSignal(this.dtypeService.retrieveDTypeDataCollectionByUri('master://Brand=' + this.data.marca_code + '/OU=01/CancellationReasons'));

    async saveReason(action: string) {
        this.control.markAllAsTouched();
        if (action === 'save') {
            const reason: CancelReason = this.reasonsData().find(rd => rd.code === this.control.value);
            let confirmSave: boolean;
            if ((this.workshiftsCantCancel().length + this.workshiftsToCancel().length) === 1) {
                confirmSave = await this.confirm(this.transloco.translate('warning'), this.transloco.translate('services.workshifts.saveCancelReasonUniqueWorkshift'));
            } else {
                confirmSave = await this.confirm(this.transloco.translate('warning'), this.transloco.translate('services.workshifts.saveCancelReason'));
            }
            if (confirmSave) {
                this.dialogRef.close(reason);
            }
        } else if (action === 'cancel') {
            const confirmCancel = await this.confirm(this.transloco.translate('warning'), this.transloco.translate('services.workshifts.confirmCancel'));
            if (confirmCancel) {
                this.dialogRef.close();
            }
        }
    }

    private confirm(title?: string, message?: string, confirm?: string, cancel?: string) {
        const dialogRef = this.fuseConfirmationService.open({
            title: title ? title : this.transloco.translate('unsavedChanges'),
            message: message ? message : this.transloco.translate('exitWithUnsavedChanges'),
            actions: {
                confirm: {
                    label: confirm ? confirm : this.transloco.translate('accept')
                },
                cancel: {
                    label: cancel ? cancel : this.transloco.translate('cancel')
                }
            }
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe((result) => {
                if (result === 'confirmed') {
                    resolve(true);
                } else {
                    resolve(false);
                }
            });
        });
    }
}
